import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Search, X, Plus, Workflow } from 'lucide-react';
import { useBreadcrumb } from 'shared/context/BreadcrumbContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import IssuesFilter from 'features/issues/components/IssuesFilter';
import BulkBar from 'shared/components/tables/BulkBar';
import TableFooter from 'shared/components/tables/TableFooter';
import { usePermissions, PERMISSIONS } from 'shared/hooks/usePermissions';
import {
  activitySearchWrapperClass, activitySearchInputClass, activityNeutralButtonClass,
  tableHeaderRowClass, tableHeaderCellClass, tableRowClass, tableDataCellClass,
} from 'features/activity/styles/dataTableStyles';
import { listWorkflows, bulkWorkflows } from 'features/agents/api/agentsApi';
import { timeAgo } from 'features/agents/lib/format';
import { AgentStatusDot } from 'features/agents/lib/status';
import { triggerMetaOf, triggerSummary } from 'features/agents/lib/triggerMeta';
import NewAgentDialog from 'features/agents/components/NewAgentDialog';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'draft_only', label: 'Draft' },
];

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Trigger as one line, shown on hover of the trigger icon: "Schedule · every day at 09:00 UTC", "Alert", "Slack: message posted". */
function triggerText(trigger) {
  const meta = triggerMetaOf(trigger);
  if (!meta) return '';
  const summary = triggerSummary(trigger);
  return summary && summary !== meta.label ? `${meta.label} · ${summary}` : meta.label;
}

/** What a bulk response says went wrong, one sentence. '' when all worked. */
export function bulkProblem(results) {
  const bad = (results || []).filter((r) => !r.ok);
  if (!bad.length) return '';
  const unpublished = bad.filter((r) => r.error === 'not_published').length;
  if (unpublished === bad.length) return `${plural(unpublished, 'agent')} not published. Publish ${unpublished === 1 ? 'it' : 'them'} first.`;
  const other = bad.find((r) => r.error !== 'not_published');
  const text = other.error === 'read_only' ? 'The example project is read-only.' : other.error === 'not_found' ? 'An agent no longer exists.' : String(other.error || 'Something failed');
  return unpublished ? `${text} ${plural(unpublished, 'agent')} not published: publish them first.` : text;
}

/**
 * The agents list, in the Alerts grammar: search + Filter (status) + New agent on
 * top; a row is the agent (status, trigger) and when it last ran. A click opens it; ticking rows brings up Enable, Disable and Delete.
 */
export default function AgentsListPage() {
  const { team } = useParams();
  const navigate = useNavigate();
  useBreadcrumb([{ label: 'Agents' }]);
  const { confirm: appConfirm } = useAppDialog();
  const canWrite = usePermissions().can(PERMISSIONS.APP_USE);

  const [rows, setRows] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  // Statuses ticked in the Filter popup; none = all. The API takes one status:
  // exactly one ticked is sent to it; two are filtered here on the loaded rows
  // (the footer then says "N loaded" and Load more keeps paging everything).
  const [statuses, setStatuses] = useState([]);
  const status = statuses.length === 1 ? statuses[0] : '';
  const clientFilter = statuses.length > 1 && statuses.length < STATUS_OPTIONS.length;
  const [choosing, setChoosing] = useState(false);
  const closeChooser = useCallback(() => setChoosing(false), []);
  const seq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  /** Loads page 1 (replacing) or the next page (appending). Stale answers are dropped. */
  const load = useCallback(async (pageNo = 1, append = false) => {
    const mine = ++seq.current;
    try {
      const { workflows, pagination } = await listWorkflows({ page: pageNo, limit: PAGE_SIZE, q: debouncedQ, status });
      if (mine !== seq.current) return;
      setRows((cur) => (append && cur ? [...cur, ...workflows.filter((w) => !cur.some((c) => c.id === w.id))] : workflows));
      setPage(pageNo);
      setTotal(pagination.total ?? workflows.length);
      setTotalPages(pagination.totalPages || 1);
      setError('');
    } catch (e) {
      if (mine !== seq.current) return;
      setError(e?.response?.data?.error || e.message || 'Failed to load agents');
      if (!append) setRows([]);
    }
  }, [debouncedQ, status]);

  useEffect(() => { setRows(null); load(1, false); }, [load]);

  const loadMore = async () => { setLoadingMore(true); await load(page + 1, true); setLoadingMore(false); };

  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  // A reload, a search or a tab can drop rows the selection still holds.
  useEffect(() => { setPicked((cur) => (rows ? new Set([...cur].filter((id) => rows.some((r) => r.id === id))) : cur)); }, [rows]);
  const visible = rows && clientFilter ? rows.filter((r) => statuses.includes(r.status)) : rows;
  const chosen = (visible || []).filter((r) => picked.has(r.id));
  const allPicked = !!visible && visible.length > 0 && visible.every((r) => picked.has(r.id));
  const toggleOne = (id) => setPicked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  /** One bulk call; what failed is said (after the reload, which clears errors), what worked is kept. */
  const bulk = async (targets, action) => {
    if (!targets.length) return;
    setBusy(true);
    let problem = '';
    try {
      const { results } = await bulkWorkflows(targets.map((r) => r.id), action);
      problem = bulkProblem(results);
    } catch (e) {
      problem = e?.response?.data?.error || e.message || 'Something failed';
    }
    setPicked(new Set());
    await load(1, false);
    if (problem) setError(problem);
    setBusy(false);
  };

  const canEnable = (r) => r.status !== 'active' && !!r.published_version_id;
  const enableChosen = () => bulk(chosen.filter(canEnable), 'enable');
  const disableChosen = () => bulk(chosen.filter((r) => r.status === 'active'), 'disable');
  const removeChosen = async () => {
    if (!chosen.length) return;
    const ok = await appConfirm(
      `${chosen.length === 1 ? `"${chosen[0].name}" will` : `${chosen.length} agents will`} be removed, with their run history.`,
      { title: chosen.length === 1 ? 'Delete this agent?' : `Delete ${chosen.length} agents?`, confirmLabel: 'Delete', danger: true },
    );
    if (ok) await bulk(chosen, 'delete');
  };

  const open = (r) => navigate(`/${encodeURIComponent(team)}/agents/${r.id}`);
  const filtered = !!debouncedQ || statuses.length > 0;
  const statusSection = { id: 'status', label: 'Status', options: STATUS_OPTIONS, selected: statuses, onChange: setStatuses, emptyIsAny: true };
  const newAgentBtn = (
    <button type="button" onClick={() => setChoosing(true)}
      className="inline-flex h-8 shrink-0 items-center gap-1 rounded-md bg-brand px-3 text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover">
      <Plus size={13} /> New agent
    </button>
  );

  return (
    <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="flex shrink-0 flex-col gap-2 bg-surface px-3 sm:px-4 pb-2 pt-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className={`${activitySearchWrapperClass} w-[240px] max-w-full`}>
            <Search size={14} className="flex-none text-[color:var(--text-muted)]" aria-hidden />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search agents…" aria-label="Search agents" className={activitySearchInputClass} />
            {q && (
              <button type="button" onClick={() => setQ('')} className="flex-none text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]" aria-label="Clear search">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <IssuesFilter sections={[statusSection]} />
          <div className="flex-1" />
          {canWrite && newAgentBtn}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="flex flex-col px-4 pb-8 pt-3">
          {error && <div role="alert" className="mb-3 rounded-md border border-[color:var(--feedback-error)] bg-danger-soft p-3 text-[12px] text-danger">{error}</div>}
          {rows === null && <PageLoader label="Loading agents" />}
          {rows !== null && visible.length === 0 && !error && !filtered && (
            <div className="flex h-48 items-center justify-center gap-3 rounded-md border border-[color:var(--border-default)] bg-surface px-6 text-center text-[12px] text-[color:var(--text-muted)]">
              No agents yet.
              {canWrite && newAgentBtn}
            </div>
          )}
          {rows !== null && visible.length === 0 && !error && filtered && (
            <div className="flex h-32 items-center justify-center rounded-md border border-[color:var(--border-default)] bg-surface px-6 text-center text-[12px] text-[color:var(--text-muted)]">
              {debouncedQ ? `Nothing matches “${debouncedQ}”.` : 'No agents in this status.'}
              {page < totalPages && (
                <button type="button" disabled={loadingMore} onClick={loadMore} className={`${activityNeutralButtonClass} ml-3`}>{loadingMore ? 'Loading…' : 'Load more'}</button>
              )}
            </div>
          )}
          {rows !== null && visible.length > 0 && (
            <>
              <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-sm">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] text-left">
                    <thead>
                      <tr className={tableHeaderRowClass}>
                        {canWrite && (
                          <th scope="col" className={`${tableHeaderCellClass} w-8`}>
                            <input type="checkbox" checked={allPicked} aria-label={allPicked ? 'Clear the selection' : 'Select all'} title={allPicked ? 'Clear the selection' : 'Select all'}
                              onChange={() => setPicked(allPicked ? new Set() : new Set(visible.map((r) => r.id)))} />
                          </th>
                        )}
                        <th scope="col" className={tableHeaderCellClass}>Name</th>
                        <th scope="col" className={tableHeaderCellClass}>Description</th>
                        <th scope="col" className={`${tableHeaderCellClass} w-[130px]`}>Last run</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((r) => {
                        const meta = triggerMetaOf(r.trigger);
                        const TrigIcon = meta?.Icon || Workflow;
                        const last = r.last_run_at || r.trigger?.last_fired_at;
                        const text = triggerText(r.trigger);
                        return (
                          <tr key={r.id} className={`${tableRowClass} cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--state-focus-ring)]`}
                            tabIndex={0} aria-label={`Open ${r.name}`} onClick={() => open(r)}
                            onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); open(r); } }}>
                            {canWrite && (
                              <td className={`${tableDataCellClass} w-8`} onClick={(e) => e.stopPropagation()}>
                                <input type="checkbox" checked={picked.has(r.id)} aria-label={`Select ${r.name}`} onChange={() => toggleOne(r.id)} />
                              </td>
                            )}
                            <td className={`${tableDataCellClass} max-w-0 w-[32%]`}>
                              <span className="flex min-w-0 items-center gap-2">
                                <AgentStatusDot status={r.status} />
                                <span className="inline-flex shrink-0" role="img" aria-label={text || 'Agent'} title={text || undefined}>
                                  <TrigIcon className="h-3.5 w-3.5 text-[color:var(--text-muted)]" aria-hidden />
                                </span>
                                <span className="truncate font-medium">{r.name}</span>
                              </span>
                            </td>
                            <td className={`${tableDataCellClass} max-w-[320px]`}>
                              <span className="block truncate text-[color:var(--text-secondary)]" title={r.description || undefined}>
                                {r.description || <span className="text-[color:var(--text-disabled)]">—</span>}
                              </span>
                            </td>
                            <td className={`${tableDataCellClass} whitespace-nowrap text-[color:var(--text-muted)]`} title={last ? new Date(last).toLocaleString() : 'Never ran'}>
                              {last ? timeAgo(last) : <span className="text-[color:var(--text-disabled)]">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <TableFooter shown={visible.length} total={clientFilter ? null : total} hasMore={page < totalPages} loading={loadingMore} onLoadMore={loadMore} />
            </>
          )}
        </div>
      </div>
      <BulkBar count={canWrite ? picked.size : 0} label="Selected agents" onClear={() => setPicked(new Set())} actions={[
        { label: 'Enable', disabled: busy || !chosen.some(canEnable), onClick: enableChosen },
        { label: 'Disable', disabled: busy || !chosen.some((r) => r.status === 'active'), onClick: disableChosen },
        { label: 'Delete', disabled: busy, onClick: removeChosen, danger: true },
      ]} />
      <NewAgentDialog open={choosing} onClose={closeChooser} />
    </main>
  );
}
